import { useEffect, useRef } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';

// Bundled at build time by deploy.sh / CRA env: REACT_APP_BUILD_VERSION
const BUILD_VERSION = (process.env.REACT_APP_BUILD_VERSION || 'dev').trim();

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 min
const TOAST_ID = 'lh-app-update';
// Ricorda quale versione server è già stata mostrata, così se l'utente ricarica
// e il bundle non si aggiorna davvero (BFCache iOS, SW cache, ecc.) non ri-spammiamo.
const SEEN_KEY = 'lh_version_seen_server';

/**
 * Polls /api/version and prompts the user to reload when a new build is live.
 * No-op in dev (BUILD_VERSION === 'dev') so local rebuilds don't spam toasts.
 */
export function useVersionCheck() {
  const promptedRef = useRef(false);

  useEffect(() => {
    if (BUILD_VERSION === 'dev') return;

    let cancelled = false;
    let timer = null;

    const forceReload = (serverVersion) => {
      // Cache-bust: alcuni browser (iOS Safari/BFCache, SW cache) ignorano un reload normale
      // e ti riservono il vecchio bundle. Aggiungere ?v=<sha> al pathname rompe quel ciclo
      // perché index.html va richiesto fresh.
      try {
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.getRegistrations().then((regs) => {
            regs.forEach((r) => r.update?.());
          });
        }
      } catch (_) { /* ignore */ }
      const url = new URL(window.location.href);
      url.searchParams.set('v', serverVersion);
      window.location.replace(url.toString());
    };

    const promptReload = (serverVersion) => {
      if (promptedRef.current) return;
      // Se in questo browser ho già mostrato il prompt per la stessa versione server,
      // non ri-spammare anche se il reload non ha aggiornato il bundle.
      try {
        if (window.localStorage.getItem(SEEN_KEY) === serverVersion) {
          promptedRef.current = true;
          return;
        }
      } catch (_) { /* localStorage può essere bloccato */ }
      promptedRef.current = true;
      try { window.localStorage.setItem(SEEN_KEY, serverVersion); } catch (_) { /* ignore */ }
      // eslint-disable-next-line no-console
      console.info('[version] new build detected', { client: BUILD_VERSION, server: serverVersion });
      toast.message('Nuova versione disponibile', {
        id: TOAST_ID,
        description: 'Ricarica per aggiornare LeagueHub.',
        duration: Infinity,
        action: {
          label: 'Aggiorna',
          onClick: () => forceReload(serverVersion),
        },
      });
    };

    const check = async () => {
      if (cancelled || document.hidden) return;
      try {
        const res = await api.get('/version', { timeout: 5000 });
        const serverVersion = (res.data?.version || '').trim();
        if (!serverVersion) return;
        // Allineato: pulisco anche il "seen" così il prossimo deploy ricomincia da capo
        if (serverVersion === BUILD_VERSION) {
          try { window.localStorage.removeItem(SEEN_KEY); } catch (_) { /* ignore */ }
          return;
        }
        promptReload(serverVersion);
      } catch (e) {
        // Silent: rete giù, BE giù, ecc. — non spammare.
      }
    };

    const onVisibility = () => { if (!document.hidden) check(); };

    check();
    timer = setInterval(check, POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);
}

export { BUILD_VERSION };
